import {IMentionData, Mention, MentionsInput} from "react-mentions";
import * as React from "react";
import {RootState} from "../../store/reducer/root/RootState";
import User from "../../model/user/User";
import {connect} from "react-redux";
import MentionUtils from "../../utils/MentionUtils";
import {FocusableProps} from "../common/props/FocusableProps";
import {UserRole} from "../../model/user/UserRole";
import {isPhoneScreen} from "../../utils/Utils";

const sanitizeHtml = require("sanitize-html");

interface IComponentProps {
	/**
	 * Text with user mentions
	 */
	value: string
	/**
	 * On change callback
	 * @param event
	 * @param {IMentionData[]} mentions
	 * @param {string} newPlainTextValue
	 */
	onChange?: (event: any, mentions: IMentionData[], newPlainTextValue: string) => void
	/**
	 * placeholder
	 */
	placeholder?: string
	/**
	 * Readonly flag
	 */
	readonly?: boolean
	/**
	 * Min height
	 */
	minHeight?: number
	/**
	 * border flag
	 */
	hasBorder?: boolean
	/**
	 * Custom class name.
	 */
	className?: string;
	/**
	 * Show only agents, admins and bdps
	 */
	onlyAgentsAdminsAndBdps?: boolean
}

interface IReduxProps {
	/**
	 * All enabled users
	 */
	users: User[];
}

type Props = IComponentProps & IReduxProps & FocusableProps;

class UserMention extends React.Component<Props, {}> {

	public static defaultProps: Partial<Props> = {
		className: "c-mentions-input",
		focus: true
	};

	constructor(props: Props) {
		super(props);

		this.filterUsers = this.filterUsers.bind(this);
		this.onChange = this.onChange.bind(this);
	}

	componentDidMount() {
		const textArea = document.querySelector(`.${this.props.className}__input`) as HTMLTextAreaElement;
		if (textArea && this.props.focus && !isPhoneScreen()) {
			textArea.focus();
		}
	}

	render() {
		const html = sanitizeHtml(MentionUtils.getPlainText(this.props.value), {
			allowedTags: ["span", "a"],
			allowedAttributes: {
				"span": ["class"],
				"a": ["href", "class", "target"]
			}
		});
		if (this.props.readonly) {
			return (
				<span className={"pre-line"} dangerouslySetInnerHTML={{__html: html}}/>
			)
		}

		return (
			<MentionsInput value={this.props.value}
						   className={this.props.className}
						   onChange={this.onChange}
						   style={this.getDefaultStyle()}
						   placeholder={this.props.placeholder}
						   allowSpaceInQuery
						   displayTransform={(id: string, display: string) => {
							   return MentionUtils.displayTransform(display)
						   }}>
				<Mention data={this.filterUsers}
						 style={defaultMentionStyle}/>
			</MentionsInput>
		)
	}


	filterUsers(query: string): IMentionData[] {
		return this.props.users.filter(u => u.searchString.indexOf(query.toLowerCase()) > -1).map(
			u => {return {id: u.id, display: u.getDisplayName()}})
	}

	onChange(event: any,
		newValue: string,
		newPlainTextValue: string,
		mentions: IMentionData[]) {
		this.props.onChange(event, mentions, newPlainTextValue)
	}

	getDefaultStyle() {
		return {
			width: "100%",
			'&multiLine': {
				input: {
					minHeight: this.props.minHeight ? this.props.minHeight : 52,
					outline: 0,
					border: this.props.hasBorder ? "solid 1px #ebedf2" : 0,
					borderRadius: "4px",
					padding: "15px",
					color: "#112363"
				},
				// The layer drawing the mentions under the text gets the input's border too, and being a little
				// wider it showed as a second frame. It keeps the width, so the text stays over its mentions.
				highlighter: {
					borderColor: "transparent"
				},
			},

			suggestions: {
				zIndex: 100,
				list: {
					backgroundColor: 'white',
					boxShadow: "0 1px 8px 0 rgba(0, 0, 0, 0.1)",
					borderRadius: "4px",
					maxHeight: "180px",
					overflowY: "auto"
				},

				item: {
					padding: '8px',
					fontSize: "13px",
					borderBottom: '1px solid rgba(0,0,0,0.05)',
					'&focused': {
						backgroundColor: '#edf4fc',
					},
				},
			}
		};
	}
}

function mapStateToProps(state: RootState, props: IComponentProps) {
	return {
		users: state.user.users.getOrElse([]).filter(u => !u.disabled &&
			(!props.onlyAgentsAdminsAndBdps || u.hasOneOfRoles([UserRole.Agent, UserRole.Admin, UserRole.BusinessDevelopmentPartner])))
	}
}

export default connect(mapStateToProps)(UserMention)

const defaultMentionStyle = {
	backgroundColor: '#d4d6e3',
	borderRadius: "4px"
};


